import React from 'react';
import './MiddleContent.css';
import { OwcProgressBar } from '@one/react';
import { resolveComponent } from '../../config/componentRegistry';
import { getDashboardLayout } from '../../config/uiConfig';

/**
 * Render a list of config-defined panels, passing each only the props it
 * declares (by name) from the shared `available` prop bag.
 */
function renderPanels(panels, available) {
  return panels.map(({ component, props = [], wrapperClassName }) => {
    const Component = resolveComponent(component);
    if (!Component) return null;

    const selectedProps = props.reduce((acc, name) => {
      acc[name] = available[name];
      return acc;
    }, {});

    const node = <Component {...selectedProps} />;
    return wrapperClassName ? (
      <div key={component} className={wrapperClassName}>
        {node}
      </div>
    ) : (
      <React.Fragment key={component}>{node}</React.Fragment>
    );
  });
}

function MiddleContent({
  groupName,
  schemaData,
  schemaLoading,
  schemaError,
  schemaSource,
  loadingInitialRelease,
  fetchReleaseVersions,
  groupChangeLoading,
  loadingReleaseChange,
  entityVersions,
  entityVersionsLoading,
  entityVersionsError,
  selectedEntityVersion,
  onEntityVersionChange,
  igUri,
  igMeta,
  igLoading,
  igError,
  igVersions,
  igVersionsLoading,
  selectedIgVersion,
  onIgVersionChange,
  hasIgSelection,
}) {
  const showLoader = loadingInitialRelease || groupChangeLoading;

  // Shared prop bag; panels pick what they need by name via uiConfig pageLayout.
  const available = {
    schemaData,
    schemaLoading,
    schemaError,
    schemaSource,
    fetchReleaseVersions,
    loadingReleaseChange,
    entityVersions,
    entityVersionsLoading,
    entityVersionsError,
    selectedEntityVersion,
    onEntityVersionChange,
    igUri,
    igMeta,
    igLoading,
    igError,
    igVersions,
    igVersionsLoading,
    selectedIgVersion,
    onIgVersionChange,
    hasIgSelection,
  };

  // Layout is resolved per group so iframe-domain groups (e.g. Clinical) render
  // their own panels; all others fall back to the default dashboard layout.
  const layout = getDashboardLayout(groupName);

  return (
    <div
      className="middle-content-container"
      style={{
        overflowY: showLoader ? 'hidden' : 'auto',
        minHeight: '100%',
      }}
    >
      <div className="middle-content-inner">
        {renderPanels(layout.header, available)}

        {showLoader ? (
          <div style={{ margin: '40px auto', textAlign: 'center' }}>
            <OwcProgressBar
              caption={
                groupChangeLoading
                  ? 'Loading data group, please wait...'
                  : 'Getting release version, please wait...'
              }
              value={undefined}
              style={{ width: '320px', margin: '0 auto' }}
            />
          </div>
        ) : (
          renderPanels(layout.body, available)
        )}
      </div>
    </div>
  );
}

export default React.memo(MiddleContent);
